import React, { useState } from 'react'
import { ProfilePrivacy, UserProfile, ProfileView } from '../types'

interface ProfilePrivacyControlsProps {
  profile: UserProfile
  privacy: ProfilePrivacy
  profileViews: ProfileView[]
  onUpdatePrivacy: (privacy: Partial<ProfilePrivacy>) => void
}

const ProfilePrivacyControls: React.FC<ProfilePrivacyControlsProps> = ({
  profile,
  privacy,
  profileViews,
  onUpdatePrivacy
}) => {
  const [activeTab, setActiveTab] = useState<'general' | 'visitors' | 'blocked'>('general')
  const [showVisitorModal, setShowVisitorModal] = useState(false)
  const [selectedTimeframe, setSelectedTimeframe] = useState<'day' | 'week' | 'month' | 'all'>('week')

  const getFilteredViews = () => {
    const now = new Date()
    let cutoffDate: Date

    switch (selectedTimeframe) {
      case 'day':
        cutoffDate = new Date(now.getTime() - 24 * 60 * 60 * 1000)
        break
      case 'week':
        cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        break
      case 'month':
        cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
        break
      default:
        cutoffDate = new Date(0)
    }

    return profileViews.filter(view => view.timestamp >= cutoffDate)
  }

  const getVisitorStats = () => {
    const filteredViews = getFilteredViews()
    const uniqueVisitors = new Set(filteredViews.map(v => v.viewerId).filter(Boolean)).size
    const anonymousViews = filteredViews.filter(v => !v.viewerId).length
    const returningVisitors = filteredViews.filter(v => {
      if (!v.viewerId) return false
      return profileViews.filter(pv => pv.viewerId === v.viewerId).length > 1
    }).length

    const sourceBreakdown = filteredViews.reduce((acc, view) => {
      acc[view.source] = (acc[view.source] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    const deviceBreakdown = filteredViews.reduce((acc, view) => {
      acc[view.deviceType] = (acc[view.deviceType] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    return {
      totalViews: filteredViews.length,
      uniqueVisitors,
      anonymousViews,
      returningVisitors,
      sourceBreakdown,
      deviceBreakdown
    }
  }

  const visitorStats = getVisitorStats()

  const handleBlockUser = (userId: string) => {
    if (!privacy.blockedUsers.includes(userId)) {
      onUpdatePrivacy({
        blockedUsers: [...privacy.blockedUsers, userId]
      })
    }
  }

  const handleUnblockUser = (userId: string) => {
    onUpdatePrivacy({
      blockedUsers: privacy.blockedUsers.filter(id => id !== userId)
    })
  }

  const renderVisitorList = () => {
    const filteredViews = getFilteredViews()
    const groupedViews = filteredViews.reduce((acc, view) => {
      const key = view.viewerId || 'anonymous'
      if (!acc[key]) {
        acc[key] = {
          viewerId: view.viewerId,
          viewerUsername: view.viewerUsername,
          views: [],
          totalDuration: 0
        }
      }
      acc[key].views.push(view)
      acc[key].totalDuration += view.duration || 0
      return acc
    }, {} as Record<string, any>)

    return Object.values(groupedViews).map((visitor: any, index) => (
      <div key={visitor.viewerId || index} className="visitor-item">
        <div className="visitor-info">
          <div className="visitor-avatar">
            {visitor.viewerId ? (
              <span className="user-avatar">👤</span>
            ) : (
              <span className="anonymous-avatar">👻</span>
            )}
          </div>
          <div className="visitor-details">
            <div className="visitor-name">
              {visitor.viewerUsername || 'Anonymous Visitor'}
            </div>
            <div className="visitor-stats">
              <span>{visitor.views.length} views</span>
              {visitor.totalDuration > 0 && (
                <span>{Math.round(visitor.totalDuration / 60)}m total</span>
              )}
            </div>
            <div className="visitor-last-seen">
              Last seen: {visitor.views[visitor.views.length - 1].timestamp.toLocaleDateString()}
            </div>
          </div>
        </div>
        
        <div className="visitor-actions">
          {visitor.viewerId && (
            <>
              <button className="view-profile-btn" title="View Profile">
                👁️
              </button>
              <button 
                className="block-user-btn" 
                title="Block User"
                onClick={() => handleBlockUser(visitor.viewerId)}
              >
                🚫
              </button>
            </>
          )}
        </div>
      </div>
    ))
  }

  const renderBlockedUsers = () => {
    return (
      <div className="blocked-users-list">
        {privacy.blockedUsers.length === 0 ? (
          <div className="empty-state">
            <p>No blocked users</p>
          </div>
        ) : (
          privacy.blockedUsers.map(userId => (
            <div key={userId} className="blocked-user-item">
              <div className="user-info">
                <span className="user-avatar">👤</span>
                <span className="username">User {userId}</span>
              </div>
              <button 
                className="unblock-btn"
                onClick={() => handleUnblockUser(userId)}
              >
                Unblock
              </button>
            </div>
          ))
        )}
      </div>
    )
  }

  return (
    <div className="privacy-controls">
      <div className="privacy-header">
        <h3>🔒 Privacy & Security</h3>
        <p>Control who can see your profile and activity</p>
      </div>

      <div className="privacy-tabs">
        {(['general', 'visitors', 'blocked'] as const).map(tab => (
          <button
            key={tab}
            className={activeTab === tab ? 'active' : ''}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'general' && '⚙️ General'}
            {tab === 'visitors' && '👁️ Visitors'}
            {tab === 'blocked' && '🚫 Blocked'}
          </button>
        ))}
      </div>

      <div className="privacy-content">
        {activeTab === 'general' && (
          <div className="general-privacy">
            <div className="privacy-section">
              <h4>👁️ Profile Visibility</h4>
              <div className="privacy-options">
                <label className="privacy-option">
                  <input
                    type="radio"
                    name="profileVisibility"
                    value="public"
                    checked={privacy.profileVisibility === 'public'}
                    onChange={(e) => onUpdatePrivacy({ profileVisibility: e.target.value as any })}
                  />
                  <div className="option-content">
                    <div className="option-title">🌍 Public</div>
                    <div className="option-description">Anyone can view your profile</div>
                  </div>
                </label>

                <label className="privacy-option">
                  <input
                    type="radio"
                    name="profileVisibility"
                    value="followers"
                    checked={privacy.profileVisibility === 'followers'}
                    onChange={(e) => onUpdatePrivacy({ profileVisibility: e.target.value as any })}
                  />
                  <div className="option-content">
                    <div className="option-title">👥 Followers Only</div>
                    <div className="option-description">Only your followers can view your profile</div>
                  </div>
                </label>

                <label className="privacy-option">
                  <input
                    type="radio"
                    name="profileVisibility"
                    value="private"
                    checked={privacy.profileVisibility === 'private'}
                    onChange={(e) => onUpdatePrivacy({ profileVisibility: e.target.value as any })}
                  />
                  <div className="option-content">
                    <div className="option-title">🔒 Private</div>
                    <div className="option-description">Only you can view your full profile</div>
                  </div>
                </label>
              </div>
            </div>

            <div className="privacy-section">
              <h4>📱 Contact & Messaging</h4>
              <div className="privacy-toggles">
                <label className="toggle-option">
                  <span>Who can message you</span>
                  <select
                    value={privacy.whoCanMessage}
                    onChange={(e) => onUpdatePrivacy({ whoCanMessage: e.target.value as any })}
                  >
                    <option value="everyone">Everyone</option>
                    <option value="followers">Followers only</option>
                    <option value="nobody">Nobody</option>
                  </select>
                </label>
              </div>
            </div>

            <div className="privacy-section">
              <h4>📊 Analytics & Activity</h4>
              <div className="privacy-toggles">
                <label className="toggle-option">
                  <input
                    type="checkbox"
                    checked={privacy.showLastActive}
                    onChange={(e) => onUpdatePrivacy({ showLastActive: e.target.checked })}
                  />
                  <span>Show when you were last active</span>
                </label>

                <label className="toggle-option">
                  <input
                    type="checkbox"
                    checked={privacy.showLocation}
                    onChange={(e) => onUpdatePrivacy({ showLocation: e.target.checked })}
                  />
                  <span>Show your location</span>
                </label>

                <label className="toggle-option">
                  <input
                    type="checkbox"
                    checked={privacy.showFollowerCount}
                    onChange={(e) => onUpdatePrivacy({ showFollowerCount: e.target.checked })}
                  />
                  <span>Show follower count</span>
                </label>

                <label className="toggle-option">
                  <input
                    type="checkbox"
                    checked={privacy.showAnalytics}
                    onChange={(e) => onUpdatePrivacy({ showAnalytics: e.target.checked })}
                  />
                  <span>Show profile analytics to others</span>
                </label>

                <label className="toggle-option">
                  <span>Who can view your analytics</span>
                  <select
                    value={privacy.whoCanViewAnalytics}
                    onChange={(e) => onUpdatePrivacy({ whoCanViewAnalytics: e.target.value as any })}
                  >
                    <option value="nobody">Nobody</option>
                    <option value="self">Only you</option>
                    <option value="followers">Your followers</option>
                    <option value="everyone">Everyone</option>
                  </select>
                </label>
              </div>
            </div>

            <div className="privacy-section">
              <h4>🔍 Search & Discovery</h4>
              <div className="privacy-toggles">
                <label className="toggle-option">
                  <input
                    type="checkbox"
                    checked={privacy.allowProfileIndexing}
                    onChange={(e) => onUpdatePrivacy({ allowProfileIndexing: e.target.checked })}
                  />
                  <span>Allow search engines to index your profile</span>
                </label>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'visitors' && (
          <div className="visitors-tab">
            <div className="visitors-header">
              <div className="visitor-stats-cards">
                <div className="stat-card">
                  <div className="stat-number">{visitorStats.totalViews}</div>
                  <div className="stat-label">Total Views</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">{visitorStats.uniqueVisitors}</div>
                  <div className="stat-label">Unique Visitors</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">{visitorStats.anonymousViews}</div>
                  <div className="stat-label">Anonymous</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">{visitorStats.returningVisitors}</div>
                  <div className="stat-label">Returning</div>
                </div>
              </div>

              <div className="visitor-controls">
                <select
                  value={selectedTimeframe}
                  onChange={(e) => setSelectedTimeframe(e.target.value as any)}
                >
                  <option value="day">Past 24 hours</option>
                  <option value="week">Past week</option>
                  <option value="month">Past month</option>
                  <option value="all">All time</option>
                </select>
              </div>
            </div>

            <div className="visitor-breakdown">
              <div className="breakdown-section">
                <h4>📱 Device Types</h4>
                <div className="breakdown-chart">
                  {Object.entries(visitorStats.deviceBreakdown).map(([device, count]) => (
                    <div key={device} className="breakdown-item">
                      <span className="breakdown-label">
                        {device === 'desktop' && '💻'} 
                        {device === 'mobile' && '📱'}
                        {device === 'tablet' && '📟'}
                        {device}
                      </span>
                      <div className="breakdown-bar">
                        <div 
                          className="breakdown-fill"
                          style={{ width: `${(count / visitorStats.totalViews) * 100}%` }}
                        />
                      </div>
                      <span className="breakdown-count">{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="breakdown-section">
                <h4>🔗 Traffic Sources</h4>
                <div className="breakdown-chart">
                  {Object.entries(visitorStats.sourceBreakdown).map(([source, count]) => (
                    <div key={source} className="breakdown-item">
                      <span className="breakdown-label">
                        {source === 'direct' && '🔗'} 
                        {source === 'search' && '🔍'}
                        {source === 'social' && '📱'}
                        {source === 'review' && '⭐'}
                        {source === 'forum' && '💬'}
                        {source === 'referral' && '🔄'}
                        {source}
                      </span>
                      <div className="breakdown-bar">
                        <div 
                          className="breakdown-fill"
                          style={{ width: `${(count / visitorStats.totalViews) * 100}%` }}
                        />
                      </div>
                      <span className="breakdown-count">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="visitor-list-section">
              <div className="section-header">
                <h4>Recent Visitors</h4>
                <button 
                  className="view-all-btn"
                  onClick={() => setShowVisitorModal(true)}
                >
                  View All
                </button>
              </div>
              <div className="visitor-list">
                {renderVisitorList().slice(0, 5)}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'blocked' && (
          <div className="blocked-tab">
            <div className="blocked-header">
              <h4>🚫 Blocked Users</h4>
              <p>Blocked users cannot view your profile or send you messages</p>
            </div>
            
            {renderBlockedUsers()}
          </div>
        )}
      </div>

      {/* Visitor Details Modal */}
      {showVisitorModal && (
        <div className="modal-overlay" onClick={() => setShowVisitorModal(false)}>
          <div className="modal-content visitor-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>👁️ Profile Visitors</h3>
              <button onClick={() => setShowVisitorModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <div className="visitor-list full-list">
                {renderVisitorList()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProfilePrivacyControls